import React, { useEffect, useRef, useState } from 'react';
import { CompanyPlanAdListItem, PlanAdListItem } from '../../../types';
import { Alert, Box, Button, Card, CardContent, CardMedia, Collapse, Dialog, IconButton, Link, List, ListItemButton, ListItemIcon, Snackbar, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useCreateOrderRequestMutation } from '../../../services/orderRequests';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { usePublishPlanAdMutation, useUnpublishPlanAdMutation } from '../../../services/planAds';
import EditIcon from '@mui/icons-material/Edit';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ReactGA from 'react-ga4';

interface PlanAdCardProps {
	planId: number;
	ad: CompanyPlanAdListItem | PlanAdListItem;
	isAuthor: boolean;
	isUnpublished: boolean;
	onDelete: () => void;
}

export const PlanAdCard = ({planId, ad, isAuthor, isUnpublished, onDelete}: PlanAdCardProps) => {
	const theme = useTheme();
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [isEditFormOpen, setIsEditFormOpen] = useState(false);
	const [isFormSubmitted, setIsFormSubmitted] = useState(false);
	const descriptionFieldRef = useRef<HTMLInputElement>(null);
	const emailFieldRef = useRef<HTMLInputElement>(null);
	const {t} = useTranslation();
	const [orderRequestSubmitMutation, {isSuccess, reset}] = useCreateOrderRequestMutation();
	const [publishPlanAdMutation, {reset: publishReset, error: publishError}] = usePublishPlanAdMutation();
	const [unpublishPlanAdMutation] = useUnpublishPlanAdMutation();

	useEffect(() => {
		if (isSuccess) {
			setIsFormSubmitted(true);
		}
	}, [isSuccess]);

	const onAskForOfferBtnClick = () => {
		setIsFormOpen(true);
		if (emailFieldRef.current) {
			setTimeout(() => {
				descriptionFieldRef.current?.focus();
			}, 100);
		}
	};

	const onCloseForm = () => {
		setIsFormOpen(false);
		setIsFormSubmitted(false);
		reset();
		if (emailFieldRef.current) {
			emailFieldRef.current.value = '';
		}
		if (descriptionFieldRef.current) {
			descriptionFieldRef.current.value = '';
		}
	};

	const onEdit = () => {
		setIsEditFormOpen(true);
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const email = formData.get('email') as string;
		const message = formData.get('additionalInfo') as string;
		orderRequestSubmitMutation({
			planId: planId,
			vendorId: ad.companyId,
			message: message,
			email: email,
		});
		setIsFormSubmitted(true);
	};

	const onVisitWebsiteClick = () => {
		ReactGA.event({
			category: 'UI',
			action: 'VisitVendorWebsite',
			label: ad.companyName,
		});
	};

	return (<>
		<Card sx={{ 
			minWidth: isSmUp ? '350px' : '300px',
			maxWidth: '350px',
			flex: isSmUp ? '1 0 350px' : '1 0 300px',
		}}>
			<Box display='flex'>
				<CardMedia
					component="img"
					sx={{ width: 151, objectFit: 'contain', p: theme.spacing(2, 0, 2, 2) }}
					image={ad.companyImgUrl}
					alt={ad.companyName}
				/>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<CardContent sx={{ flex: '1 0 auto' }}>
						<Typography variant='subtitle1' component="div" sx={{ color: 'text.primary', maxHeight: 144, overflow: 'hidden' }}>{ad.companyName}</Typography>
						<Typography
							variant='caption'
							component="div"
							sx={{ color: 'text.secondary', maxHeight: 144, overflow: 'hidden' }}
						>
							{ad.description}
						</Typography>
					</CardContent>
				</Box>
			</Box>
			<Box sx={{ p: 2, pt: 0 }}>
				{ad.isEmailFormEnabled && <Collapse in={!isFormOpen}>
					<Button variant={isFormOpen ? 'text' : 'contained'} color="primary" size='large' fullWidth onClick={onAskForOfferBtnClick} endIcon={<ExpandMoreIcon />}>
						{t('planAdCard.askForOffer')}
					</Button>
				</Collapse>}
				<Collapse in={isFormOpen}>
					<Box display="flex" justifyContent="flex-end" sx={{mt: -6}}>
						<IconButton color="primary" size='small' onClick={onCloseForm}>
							<CloseIcon />
						</IconButton>
					</Box>
					<form onSubmit={handleSubmit}>
						<Box mt={2} display="flex" flexDirection="column" gap={2}>
							<TextField
								inputRef={descriptionFieldRef}
								size="small"
								label={t('planAdCard.additionalInfoLabel')}
								placeholder={t('planAdCard.additionalInfoPlaceholder')}
								name="additionalInfo"
								type="textarea"
								multiline
								minRows={2}
								fullWidth
								disabled={isFormSubmitted}
								required
							/>
							<TextField
								label={t('planAdCard.emailLabel')}
								name="email"
								type="email"
								required
								fullWidth
								inputRef={emailFieldRef}
								autoFocus
								disabled={isSuccess}
							/>
							<Button type="submit" variant="contained" color="primary" size='large' disabled={isFormSubmitted}>
								{isFormSubmitted ? t('planAdCard.orderRequestSubmitted') : t('planAdCard.startConversation')}
							</Button>
						</Box>
					</form>
					{isSuccess && <Typography variant="body2" mt={2}>{t('planAdCard.willBeInTouch')}</Typography>}
				</Collapse>
				{ad.website && <Collapse in={!isFormOpen}>
					{ad.isEmailFormEnabled && <Typography variant="body2" mt={1} textAlign="center">{t('planAdCard.or')}</Typography>}
					<Button component={Link} href={ad.website} variant="contained" fullWidth color={ad.isEmailFormEnabled ? 'inherit' : 'primary'} sx={{ mt: 1 }} startIcon={<ArrowOutwardIcon />} target='_blank' onClick={onVisitWebsiteClick}>
						{t('planAdCard.visitWebsite')}
					</Button>
				</Collapse>}
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 2 }}>
				{isAuthor && onEdit && (
					<IconButton 
						size="small" 
						onClick={onEdit}
					>
						<EditIcon />
					</IconButton>
				)}
			</Box>
			{isUnpublished && <Alert severity="warning" sx={{mt: 2}}>{t('planAdCard.unpublished')}</Alert>}
		</Card>
		<Dialog open={isEditFormOpen} onClose={() => setIsEditFormOpen(false)}>
			<List>
				<ListItemButton
					onClick={onDelete}
				>
					<ListItemIcon>
						<DeleteIcon />
					</ListItemIcon>
					{t('planAdCard.delete')}
				</ListItemButton>
				<ListItemButton 
					onClick={() => {
						publishPlanAdMutation({id: ad.id});
					}}
					disabled={!isUnpublished}
				>
					<ListItemIcon>
						<PlayIcon />
					</ListItemIcon>
					{t('planAdCard.publish')}
				</ListItemButton>
				<ListItemButton 
					onClick={() => {
						unpublishPlanAdMutation({id: ad.id});
					}}
					disabled={isUnpublished}
				>
					<ListItemIcon>
						<StopIcon />
					</ListItemIcon>
					{t('planAdCard.unpublish')}
				</ListItemButton>
			</List>
		</Dialog>
		<Snackbar open={Boolean(publishError)} autoHideDuration={6000} onClose={publishReset}>
			<Alert severity="error">{(publishError as any)?.data?.message}</Alert>
		</Snackbar>
	</>
	);
};
